import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { axiosGet } from '../api/axios';
import { toast } from 'react-toastify';

const getEligibilityFile = async (clientId: number, datasetId: number): Promise<{ blob: Blob; filename: string }> => {
  const response = await axiosGet(`/clients/${clientId}/eligibility_datasets/${datasetId}/download`, {}, 'v3');
  const contentDisposition = response.headers['content-disposition'];
  const filename = contentDisposition ? contentDisposition.split('filename=')[1] : 'unknown.csv';
  return { blob: new Blob([response.data], { type: 'text/csv' }), filename };
};

const getEligibilityErrorFile = async (datasetId: number): Promise<{ blob: Blob; filename: string }> => {
  const response = await axiosGet(`/eligibility_events/${datasetId}/errors`, {}, 'v3');
  const contentDisposition = response.headers['content-disposition'];
  const filename = contentDisposition ? contentDisposition.split('filename=')[1] : 'eligibility-errors.csv';
  return { blob: new Blob([response.data], { type: 'text/csv' }), filename };
};

const handleFileDownload = (data: { blob: Blob; filename: string }, successMessage: string) => {
  const downloadLink = document.createElement('a');
  const url = URL.createObjectURL(data.blob);
  downloadLink.href = url;
  downloadLink.download = data.filename;
  document.body.appendChild(downloadLink);
  downloadLink.click();
  document.body.removeChild(downloadLink);
  URL.revokeObjectURL(url);
  toast.success(successMessage);
};

const handleError = (error: any) => {
  let errorMessage = 'Error downloading file';
  if (error?.response?.data?.error) {
    errorMessage = error.response.data.error;
  }
  toast.error(errorMessage);
};

export const useEligibilityFile = () => {
  const queryClient = useQueryClient();

  return {
    downloadFile: async (clientId: number, datasetId: number) => {
      try {
        const result = await queryClient.fetchQuery(
          ['eligibilityFile', clientId, datasetId],
          () => getEligibilityFile(clientId, datasetId),
          { retry: 0 },
        );
        handleFileDownload(result, `File ${result.filename} downloaded successfully`);
      } catch (error) {
        handleError(error);
      }
    },
    isFetching: Boolean(queryClient.isFetching()),
  };
};

export const useEligibilityErrorFile = () => {
  const queryClient = useQueryClient();

  return {
    downloadErrorFile: async (dId: number) => {
      try {
        const result = await queryClient.fetchQuery(['eligibilityErrorFile', dId], () => getEligibilityErrorFile(dId), {
          retry: 0,
        });
        handleFileDownload(result, `Error file ${result.filename} downloaded successfully`);
      } catch (error) {
        handleError(error);
      }
    },
    isFetching: Boolean(queryClient.isFetching()),
  };
};

export const useCancelEligibilityFile = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ clientId, datasetId }: { clientId: number; datasetId: number }) =>
      axiosGet(`/clients/${clientId}/eligibility_datasets/${datasetId}/cancel`, {}, 'v3'),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['eligibilityFile']);
      },
      onError: () => {
        queryClient.invalidateQueries(['eligibilityFile']);
      },
    },
  );
};

export interface EligibilityEventResponse {
  count: number;
  data: EligibilityEvent[];
}

export interface EligibilityEvent {
  client_id: number;
  client_names: string[];
  completed_at: string;
  created_at: string;
  eligibility_dataset_id: number;
  package_ids: number[];
  packages_names?: string[];
  status: EligibilityEventStatus;
}

interface EligibilityEventRequest {
  clientId?: number;
  startDate?: string;
  endDate?: string;
}

// Add status enum to get readable names for the status
export enum EligibilityEventStatus {
  COMPLETED = 'completed',
  PROCESSING_FAILED = 'processing_failed',
  IN_PROGRESS = 'in_progress',
}

const getEligibilityEvents = async (request: EligibilityEventRequest): Promise<EligibilityEventResponse> => {
  const params = {
    client_id: request.clientId ?? undefined,
    start_date: request.startDate ?? undefined,
    end_date: request.endDate ?? undefined,
  };

  const response = await axiosGet(`/eligibility_events`, params, 'v3');
  return response.data;
};

export const useEligibilityEvents = (requestParams: EligibilityEventRequest) => {
  const { data, isLoading, error, refetch, isFetching } = useQuery(
    ['eligibilityEvents', requestParams],
    () => getEligibilityEvents(requestParams),
    {
      refetchOnWindowFocus: false,
    },
  );
  return { data, isLoading, error, refetch, isFetching };
};
